import {Box, Button, Grid, Typography} from '@mui/material';
import React, {useEffect, useMemo, useState} from 'react';
import {useAccountContext} from '../../../contexts/AccountContext';
import usePlanNamesProviders from '../../../hooks/usePlanNamesProviders';
import {UPDATES} from '../../../shared/UPDATES';
import AutoField from '../../../components/AutoForm/AutoFieldNew';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {
  isAuthorityHeldField,
  isDataGatherField,
  transferFullField as fullField,
  transferInSpecieSourceField as inSpecieSourceField,
  transferShellAccountNumberField,
  transferShellAmountField,
  transferShellPlanValueField,
  transferSourceType,
} from '../RecommendsTransfer/transferFields';
import type {newPlanFields} from './setUpNewFields';
import {
  setUpNewPlanProviderField as recommendPlanProviderField,
  setUpNewPlanTypeField as recommendPlanTypeField,
} from './setUpNewFields';

import {Panel} from '../../../components/Panel/Panel';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import {PlanContactField} from './PlanContactField';
import {Divide} from '../../../components/Layout/Layout';
import {
  useAutoField,
  useAutoForm,
  AutoFieldHelpers,
  SetFieldValueWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import {useSourcesAndDestinationPots} from '../../../hooks/useSourcesAndDestinationPots';
import {InitialFeeField} from '../CreateRecommend/InitialFeesField';
import {ScrollableModal} from '../../../components/Common/ScrollableModal';
import validateUUID from '../../../utils/validateUUID';

export interface ShellPlanProps {
  id?: string;
  formikShellPlanKey: string;
}

export const deriveIsLisaShellPlan = ({
  lisaId,
  values,
  planTypeFieldValue,
  sourcesAndDestinations,
  setFieldValue,
}: {
  lisaId: string | undefined;
  values: recommendFieldsType;
  planTypeFieldValue: string | undefined;
  sourcesAndDestinations: {
    id: string;
    productName?: {formattedName?: string | null} | null;
  }[];
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
}) => {
  let isLisa = false;
  if (!lisaId || !values.transfer?.destinationAccount) {
    isLisa = false;
  } else if (values.transfer?.destinationAccount === 'newPlan') {
    isLisa = planTypeFieldValue === lisaId;
  } else {
    isLisa =
      sourcesAndDestinations.find((x) => x.id === values.transfer?.destinationAccount)
        ?.productName?.formattedName === 'L-ISA';
  }
  setFieldValue(UPDATES._600, 'isLisa', isLisa);
  return isLisa;
};

// export const setDefaultShellPlanType = ({
//   mainPlanTypeValue,
//   shellPlanPlanTypeFieldValue,
//   loadingOvRecommend,
//   recommendDetails,
//   shellProducts,
//   shellPlanPlanTypeHelpers,
// }: {
//   mainPlanTypeValue: recommendFieldsType['mainPlanType'];
//   shellPlanPlanTypeFieldValue: transferSourceType['planType'];
//   loadingOvRecommend: boolean;
//   recommendDetails: any;
//   shellProducts: {label: string; value: string}[] | undefined;
//   shellPlanPlanTypeHelpers: AutoFieldHelpers<transferSourceType['planType']>;
// }) => {
//   if (mainPlanTypeValue !== 'pension') {
//     return;
//   }
//   if (
//     !shellPlanPlanTypeFieldValue &&
//     !loadingOvRecommend &&
//     !recommendDetails &&
//     shellProducts?.length
//   ) {
//     const defaultPlanType = shellProducts.find((x) =>
//       x.label.toLocaleLowerCase().includes('gia gross')
//     );
//     if (defaultPlanType) {
//       shellPlanPlanTypeHelpers.setValue(UPDATES._601, defaultPlanType.value, true);
//     }
//   }
// };

export const setShellFullAmount = ({
  shellPlanField,
  shellPlanAmountHelpers,
}: {
  shellPlanField: {value: transferSourceType};
  shellPlanAmountHelpers: AutoFieldHelpers<transferSourceType['singleClientAmount']>;
}) => {
  if (shellPlanField.value.full && shellPlanField.value.planValue) {
    shellPlanAmountHelpers.setValue(UPDATES._602, shellPlanField.value.planValue);
  }
};

export const ShellPlanServicingRights: React.FC<ShellPlanProps> = ({
  formikShellPlanKey: formikKeyPrefix,
  id,
}) => {
  const [shellPlanField] = useAutoField<transferSourceType>(`${formikKeyPrefix}`);
  const {isSPPDA} = useRecommendContext();
  if (isSPPDA) {
    return null;
  }
  return (
    <>
      <Grid alignItems="flex-start" container direction="row" spacing={2}>
        <Grid item xs={6}>
          <AutoField
            field={{...isDataGatherField, id: `${formikKeyPrefix}.isDataGather`}}
          />
        </Grid>
        {shellPlanField.value.isDataGather === 'yes' ? (
          <Grid item xs={6}>
            <AutoField
              field={{...isAuthorityHeldField, id: `${formikKeyPrefix}.isAuthorityHeld`}}
            />
          </Grid>
        ) : null}
      </Grid>
      <Divide />
    </>
  );
};
export const ShellPlan: React.FC<ShellPlanProps> = ({
  formikShellPlanKey: formikKeyPrefix,
  id,
}) => {
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  const [planTypeField] = useAutoField<newPlanFields['planType']>(
    `transfer.newPlan.planType`
  );

  const [shellPlanField] = useAutoField<transferSourceType>(`${formikKeyPrefix}`);
  const [shellPlanAmountField, shellPlanAmountMeta, shellPlanAmountHelpers] =
    useAutoField<transferSourceType['singleClientAmount']>(
      `${formikKeyPrefix}.singleClientAmount`
    );

  const {
    productProviders,
    queryGetProductProviders,
    shellProducts,
    queryGetProducts,
    lisaId,
  } = usePlanNamesProviders();
  const {accountId, pots} = useAccountContext();

  useEffect(() => {
    if (!productProviders) {
      queryGetProductProviders({
        variables: {
          accountId: validateUUID(accountId),
        },
      });
    }
  }, [productProviders]);

  useEffect(() => {
    if (!shellProducts) {
      queryGetProducts({
        variables: {},
      });
    }
  }, [shellProducts]);

  // useEffect(() => {
  //   setDefaultShellPlanType({
  //     mainPlanTypeValue: mainPlanTypeField.value,
  //     shellPlanPlanTypeFieldValue: shellPlanPlanTypeField.value,
  //     loadingOvRecommend,
  //     recommendDetails,
  //     shellProducts,
  //     shellPlanPlanTypeHelpers,
  //   });
  // }, [
  //   recommendDetails,
  //   loadingOvRecommend,
  //   shellProducts?.length,
  //   shellPlanPlanTypeField.value,
  //   mainPlanTypeField.value,
  // ]);

  useEffect(() => {
    setShellFullAmount({shellPlanField, shellPlanAmountHelpers});
  }, [shellPlanField.value.planValue, shellPlanField.value.full]);
  const [showNewProviderModal, setShowNewProviderModal] = useState(false);

  const {sourcesAndDestinations} = useSourcesAndDestinationPots();
  const isLisaDestination = useMemo(() => {
    return deriveIsLisaShellPlan({
      lisaId,
      values,
      planTypeFieldValue: planTypeField.value,
      sourcesAndDestinations,
      setFieldValue,
    });
  }, [
    lisaId,
    planTypeField.value,
    values.transfer?.destinationAccount,
    sourcesAndDestinations.length,
  ]);

  return (
    <Box mt={1} mb={1}>
      <ScrollableModal
        open={showNewProviderModal}
        onClose={() => setShowNewProviderModal(false)}
      >
        <Panel title="Request new provider">
          <Typography>
            Please contact our support desk if you require an additional provider to be
            added:{' '}
          </Typography>
          <Button
            style={{textTransform: 'lowercase'}}
            href="mailto:data@saltus.co.uk"
            type="button"
            variant="text"
          >
            data@saltus.co.uk
          </Button>
        </Panel>
      </ScrollableModal>
      <Grid alignItems="flex-start" container direction="row" spacing={2}>
        <Grid item xs={6}>
          <PlanContactField formikKeyPrefix={formikKeyPrefix} />
        </Grid>

        <Grid item xs={6}>
          <AutoField
            field={{
              ...recommendPlanProviderField,
              id: `${formikKeyPrefix}.planProvider`,
              options: (productProviders || []).filter(
                (provider) => !provider.label?.toLowerCase().includes('saltus')
              ),
            }}
          />
          <Button
            onClick={() => setShowNewProviderModal(true)}
            type="button"
            variant="text"
            size="small"
            style={{marginBottom: 0}}
          >
            Request new provider
          </Button>
        </Grid>
      </Grid>
      <Grid alignItems="flex-start" container direction="row" columnSpacing={2}>
        <Grid item xs={6}>
          <AutoField
            field={{
              ...recommendPlanTypeField,
              id: `${formikKeyPrefix}.planType`,
              options: shellProducts || [],
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <AutoField
            field={{
              ...transferShellAccountNumberField,
              id: `${formikKeyPrefix}.accountNumber`,
            }}
          />
        </Grid>
      </Grid>
      <Grid alignItems="flex-start" container direction="row" mt={1} spacing={2}>
        <Grid item xs={6}>
          <AutoField
            field={{
              ...transferShellPlanValueField,
              id: `${formikKeyPrefix}.planValue`,
            }}
          />
        </Grid>
      </Grid>
      <Divide />
      <ShellPlanServicingRights formikShellPlanKey={formikKeyPrefix} />

      <Grid alignItems="flex-start" container direction="row" spacing={2}>
        <Grid item xs={6}>
          <AutoField
            field={{
              ...transferShellAmountField,
              id: `${formikKeyPrefix}.singleClientAmount`,
              disabled: shellPlanField.value.full,
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <InitialFeeField
            formikKeyPrefixWithoutFee={`${formikKeyPrefix}`}
            disabled={isLisaDestination} // No Initial fee on transferring into a LISA
          />
        </Grid>
        <Grid item xs={6}>
          <AutoField
            field={{
              ...fullField,
              id: `${formikKeyPrefix}.full`,
              label: 'Full transfer?',
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <AutoField
            field={{
              ...inSpecieSourceField,
              id: `${formikKeyPrefix}.inSpecie`,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
